import {
    useLayoutEffect,
    useEffect,
    useState,
    Fragment
} from "react";
import {
    ActivityIndicator,
    TouchableOpacity,
    Platform,
    Share,
    Image,
    Modal,
    View
} from "react-native";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitSnackBar,
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    MarkdownViewer,
    PageContainer,
    Text
} from "ncore-ui-kit";
import {
    useNavigation,
    useRoute,
    type RouteProp
} from "@react-navigation/native";
import {
    useSafeAreaInsets
} from "react-native-safe-area-context";
import type {
    NewsData
} from "../../../../services/restAPI/actions/getNews/types";
import type {
    NewsCategoriesObjectType
} from "../../../../services/restAPI/actions/getNewsCategories/types";
import type AppStackParamList from "../../../../types/navigation/app";
import type {
    NativeStackNavigationProp
} from "@react-navigation/native-stack";
import {
    CalendarIcon,
    ChevronLeft,
    Share2,
    XIcon
} from "lucide-react-native";
import moment from "moment";
import {
    GestureHandlerRootView
} from "react-native-gesture-handler";
import {
    ZoomableImage,
    NewsCard
} from "../../../../components";
import {
    RESTService
} from "../../../../services/restAPI";
import {
    storage
} from "../../../../storage";

const NewsDetail = () => {
    const navigation = useNavigation<NativeStackNavigationProp<AppStackParamList>>();

    const route = useRoute<RouteProp<AppStackParamList, "NewsDetail">>();

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const {
        radiuses,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const inset = useSafeAreaInsets();

    const {
        newsID
    } = route.params;

    const [
        otherNewsData,
        setOtherNewsData
    ] = useState<NewsData[]>([]);

    const [
        loading,
        setLoading
    ] = useState(true);

    const [
        data,
        setData
    ] = useState<NewsData>();

    const [
        isLightboxVisible,
        setIsLightboxVisible
    ] = useState(false);

    const [
        newsCategoriesData,
        setNewsCategoriesData
    ] = useState<NewsCategoriesObjectType[]>([]);

    const handleShare = () => {
        if(!data) {
            return;
        }

        const shareUrl = `https://app.gencmusiadkonya.org/news/${newsID}`;
        const shareMessage = `${data.title}\n\n${shareUrl}`;

        if(Platform.OS === "web") {
            if(typeof navigator !== "undefined" && typeof navigator.share === "function") {
                navigator.share({
                    title: data.title,
                    text: data.title,
                    url: shareUrl
                }).catch(() => {
                });
                return;
            }

            if(typeof navigator !== "undefined" && navigator.clipboard && typeof navigator.clipboard.writeText === "function") {
                navigator.clipboard.writeText(shareUrl).then(() => {
                    NCoreUIKitSnackBar.open({
                        title: localize("linkCopied")
                    });
                }).catch(() => {
                });
                return;
            }
        }

        Share.share({
            message: shareMessage,
            title: data.title,
            url: shareUrl
        }).catch(() => {
        });
    };

    useEffect(() => {
        getNewsCategories();
        getNewsDetail();
    }, []);

    useLayoutEffect(() => {
        navigation.setOptions({
            headerShown: true,
            header: () => renderHeader()
        });
    }, [
        navigation,
        inset,
        colors,
        spaces,
        data
    ]);

    const getNewsDetail = () => {
        RESTService.action("GetNewsDetail", {
            language: storage.getString("language") ?? "tr-TR",
            newsID: newsID
        })
            .then((res) => {
                if(res.code === 200 || res.code === 201 || res.code === 202) {
                    setData(res.payload);
                }
                getOtherNews();
            }).catch((err) => {
                console.log(err);
                setLoading(false);
            });
    };

    const getOtherNews = () => {
        RESTService.action("GetNews", {
            language: storage.getString("language") ?? "tr-TR",
            page: 1
        }).then((res) => {
            if(res.code === 200 || res.code === 201 || res.code === 202) {
                setOtherNewsData(res.payload.filter((n: NewsData) => n._id !== newsID).slice(0, 3));
            }
        }).catch((err) => {
            console.log(err);
        }).finally(() => {
            setLoading(false);
        });
    };

    const getNewsCategories = () => {
        RESTService.action("GetNewsCategories", null)
            .then((res) => {
                if(res.code === 200 || res.code === 201 || res.code === 202) {
                    setNewsCategoriesData(res.payload);
                }
            }).catch((err) => {
                console.log(err);
            });
    };

    const renderHeader = () => {
        return <View
            style={[
                stylesheet.headerContainer,
                {
                    paddingTop: inset.top + spaces.spacingMd,
                    paddingBottom: spaces.spacingMd,
                    paddingHorizontal: spaces.spacingMd,
                    backgroundColor: colors.content.container.default
                }
            ]}
        >
            <TouchableOpacity
                style={[
                    stylesheet.backButton,
                    {
                        padding: spaces.spacingSm
                    }
                ]}
                onPress={() => {
                    navigation.goBack();
                }}
            >
                <ChevronLeft
                    color={colors.content.icon.default}
                    size={30}
                />
            </TouchableOpacity>
            <TouchableOpacity
                style={[
                    stylesheet.shareButton,
                    {
                        padding: spaces.spacingSm
                    }
                ]}
                onPress={handleShare}
            >
                <Share2
                    color={colors.content.icon.default}
                    size={24}
                />
            </TouchableOpacity>
        </View>;
    };

    const renderContent = () => {
        if(!data) {
            return null;
        }

        return <Fragment>
            <TouchableOpacity
                activeOpacity={0.8}
                onPress={() => setIsLightboxVisible(true)}
                style={{
                    ...stylesheet.coverPhotoContainer,
                    marginBottom: spaces.spacingMd
                }}
            >
                <Image
                    source={{
                        uri: data.coverPhotoURL
                    }}
                    resizeMode="cover"
                    style={[
                        stylesheet.coverPhoto,
                        {
                            borderRadius: radiuses.xl
                        }
                    ]}
                />
            </TouchableOpacity>
            <Text
                variant="titleLargeSize"
                style={{
                    marginBottom: spaces.spacingMd
                }}
            >
                {data.title}
            </Text>
            <View
                style={[
                    stylesheet.infoContainer,
                    {
                        marginBottom: spaces.spacingMd
                    }
                ]}
            >
                <CalendarIcon
                    color={colors.content.icon.mid}
                    style={[
                        {
                            marginRight: spaces.spacingXs
                        }
                    ]}
                    size={14}
                />
                <Text
                    variant="bodyMediumSize"
                    color="mid"
                >
                    {moment(data.createdAt).format("MMMM DD")}
                </Text>
            </View>
            <MarkdownViewer
                style={{
                    marginBottom: spaces.spacingXl
                }}
                content={data.content}
            />
            {otherNewsData && otherNewsData.length > 0 && (
                <Fragment>
                    <View
                        style={[
                            stylesheet.otherNewsHeader,
                            {
                                marginBottom: spaces.spacingMd,
                                marginTop: spaces.spacingLg
                            }
                        ]}
                    >
                        <Text
                            variant="headlineSmallSize"
                            color="high"
                        >
                            {localize("otherNews") === "otherNews" ? "Diğer Haberler" : localize("otherNews")}
                        </Text>
                        <TouchableOpacity
                            onPress={() => {
                                navigation.navigate("MainTabs", {
                                    screen: "HomeTab",
                                    params: {
                                        screen: "News"
                                    }
                                });
                            }}
                        >
                            <Text
                                variant="labelLargeSize"
                                color="mid"
                                style={stylesheet.allText}
                            >
                                {localize("all")}
                            </Text>
                        </TouchableOpacity>
                    </View>
                    {otherNewsData.map((newsItem) => {
                        let displayTitle = "";
                        if (newsItem.categoryIDs && newsItem.categoryIDs.length > 0) {
                            const titles = newsItem.categoryIDs
                                .map(id => newsCategoriesData.find(c => c._id === id)?.titleKey)
                                .filter(Boolean)
                                .map(key => localize(key as Parameters<typeof localize>[0]));

                            displayTitle = titles.join(", ");
                            if (displayTitle.length > 30) {
                                displayTitle = `${displayTitle.substring(0, 27)}...`;
                            }
                        }

                        return <NewsCard
                            key={newsItem._id}
                            _id={newsItem._id}
                            title={newsItem.title}
                            shortContent={newsItem.shortContent}
                            coverPhotoURL={newsItem.coverPhotoURL}
                            createdAt={moment(newsItem.createdAt).format("MMMM YY")}
                            type={displayTitle}
                            onPress={() => {
                                navigation.push("NewsDetail", {
                                    newsID: newsItem._id
                                });
                            }}
                        />;
                    })}
                </Fragment>)}
        </Fragment>;
    };

    const renderLightBox = () => {
        if(!data?.coverPhotoURL) {
            return null;
        }
        return <Modal
            visible={isLightboxVisible}
            onRequestClose={() => setIsLightboxVisible(false)}
            animationType="fade"
            transparent={true}
        >
            <View
                style={[
                    stylesheet.modalOverlay,
                    {
                        backgroundColor: colors.content.container.overlay
                    }
                ]}
            >
                <TouchableOpacity
                    style={[
                        stylesheet.closeButton,
                        {
                            top: spaces.spacingLg + inset.top,
                            right: spaces.spacingLg,
                            padding: spaces.spacingMd,
                            backgroundColor: colors.content.icon.disabledWhite,
                            borderRadius: radiuses.full
                        }
                    ]}
                    onPress={() => setIsLightboxVisible(false)}
                >
                    <XIcon
                        color={colors.content.icon.default}
                        size={24}
                    />
                </TouchableOpacity>

                <ZoomableImage
                    uri={data.coverPhotoURL}
                />
            </View>
        </Modal>;
    };

    if(loading) {
        return <PageContainer
            style={stylesheet.loadingContainer}
        >
            <ActivityIndicator
                size={40}
                color={colors.content.icon.default}
            />
        </PageContainer>;
    }

    return <GestureHandlerRootView
        style={stylesheet.container}
    >
        <PageContainer
            isScrollable={true}
        >
            {renderContent()}
            {renderLightBox()}
        </PageContainer>
    </GestureHandlerRootView>;
};
export default NewsDetail;
